/**
 * Returns the short time zone name (e.g., 'CET', 'CEST', 'MEZ', 'MESZ')
 * for a specific date and time zone.
 *
 * @param {Date} date The date object to check.
 * @param {string} locale The locale to use for the abbreviation (e.g., 'en' for CET/CEST, 'de' for MEZ/MESZ).
 * @param {string} timeZone The IANA time zone identifier (must be 'Europe/Berlin').
 * @returns {string} The short time zone abbreviation.
 */
// eslint-disable-next-line prefer-custom-date-types/prefer-custom-date-types
export function getShortTimeZoneName(date: Date, locale: string, timeZone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: timeZone,
      timeZoneName: "short",
    });

    const parts = formatter.formatToParts(date);
    const timezonePart = parts.find((part) => part.type === "timeZoneName");

    if (timezonePart) {
      return timezonePart.value;
    }

    return timeZone; // Fallback if part not found
  } catch (error) {
    // Optional: integrate logger service here
    return timeZone; // Silent fallback on error
  }
}

/**
 * Returns the full (long) time zone name (e.g., 'Central European Standard Time', 'Central European Summer Time',
 * or in German 'Mitteleuropäische Normalzeit', 'Mitteleuropäische Sommerzeit') for a specific date and time zone.
 *
 * @param {Date} date The date object to check.
 * @param {string} locale The locale to use for the full name (e.g., 'en', 'de').
 * @param {string} timeZone The IANA time zone identifier (e.g., 'Europe/Berlin').
 * @returns {string} The full time zone name or the timeZone identifier as fallback.
 */
// eslint-disable-next-line prefer-custom-date-types/prefer-custom-date-types
export function getFullTimeZoneName(date: Date, locale: string, timeZone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: "long",
    });
    const parts = formatter.formatToParts(date);
    const timezonePart = parts.find((part) => part.type === "timeZoneName");
    if (timezonePart?.value) {
      return timezonePart.value;
    }
    return timeZone; // Fallback: no specific part found
  } catch (error) {
    // Optional: integrate logger service here
    return timeZone; // Silent fallback on error
  }
}

/**
 * Returns the formatted time zone offset (e.g., 'GMT+01:00', 'GMT-05:00', 'GMT')
 * for a specific date and time zone.
 *
 * @param {Date} date The date object to check.
 * @param {string} locale The locale to use for formatting (e.g., 'en', 'de').
 * @param {string} timeZone The IANA time zone identifier (e.g., 'Europe/Berlin').
 * @returns {string} The formatted time zone offset.
 */
// eslint-disable-next-line prefer-custom-date-types/prefer-custom-date-types
export function getFormattedTimeZoneOffset(date: Date, locale: string, timeZone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: "longOffset",
    });
    const parts = formatter.formatToParts(date);
    const timezonePart = parts.find((part) => part.type === "timeZoneName");

    if (timezonePart?.value) {
      return timezonePart.value;
    }

    return "GMT"; // Fallback
  } catch (error) {
    // Optional: integrate logger service here
    return "GMT"; // Silent fallback on error
  }
}
